// src/hooks/usePmt3FarmingBasicsData.ts
import { useState, useEffect, useContext } from 'react';
import { Timestamp } from 'firebase/firestore'; // Keep Timestamp for type hints if needed
import { usePMT3Selection } from '@/context/PMT3SelectionContext';
import { differenceInDays } from 'date-fns';
import { fetchLatestOldValuesMap } from '@/services/pmt3OldValuesService';
import {
    fetchProductData,
    fetchFarmingHistoryData,
    fetchFarmingGroupData,
    fetchGroupAsins,
    fetchFarmingCostOverrideData
} from '@/services/pmt3DashboardDataService'; // Import new service functions

// --- Interfaces (Keep existing interfaces: ProductData, FarmingHistoryData, OverrideLedgerData, FarmingGroupData, FarmingBasicsDisplayData, GroupAsinInfo) ---
// Define interfaces for the data structures we expect
interface ProductData {
  'Days Since Farming'?: number;
  'Times Farmed'?: number;
  'Time Farming, if Actively Farming'?: number;
  'Final Farming Cost Per New CST'?: number;
  'Farming Group'?: number | string;
  'Share Terms in Farming Group'?: string;
  'Broad Seeds URL'?: string;
  'Broad Seeds Type'?: string;
}

interface FarmingHistoryData {
  'Lifetime Days Farming'?: number;
}

// Represents the structure of the 'Farming Cost Per New CST Override' subdocument
interface OverrideLedgerData {
  'New Value'?: string | number;
  'Date'?: string; // mm/dd/yyyy format
  'date'?: string; // Longer string format with timezone
}

interface FarmingGroupData {
    id: string; // ASIN
    'Farming Group'?: number;
    'Product Name'?: string;
}

// Interface for the structured data returned by the hook
export interface FarmingBasicsDisplayData {
  daysSinceFarming: number | string;
  timesFarmed: number | string;
  timesFarmedOld: number | string;
  timeFarmingActive: number | string;
  lifetimeDaysFarming: number | string;
  lifetimeDaysFarmingOld: number | string;
  lifetimeTermsFarming: string; // Value is 'NA'
  lifetimeTermsFarmingOld: number | string; // Get from oldValues
  finalCostPerCst: number | string;
  finalCostPerCstOld: number | string;
  finalCostPerCstOverride: number | string;
  finalCostPerCstOverrideDaysAgo: number | string;
  farmingGroup: number | string;
  farmingGroupOld: number | string;
  otherAsinsInGroup: number | string;
  shareTermsInGroup: string;
  shareTermsInGroupOld: string;
  broadSeedsUrl: string | null; // URL or null
  broadSeedsType: string;
  broadSeedsTypeOld: string;
}

export interface GroupAsinInfo {
    productName: string;
    asin: string;
}


// Helper to safely get nested properties from the oldValues map using bracket notation
const getNestedValue = (obj: any, key: string, defaultValue: any = '-') => {
    if (obj === null || obj === undefined || typeof obj !== 'object') {
        return defaultValue;
    }
    const value = obj[key]; // Access using bracket notation
    return value === null || value === undefined || value === '' ? defaultValue : value;
};


// Helper to parse potentially different date formats
const parseOverrideDate = (overrideData: OverrideLedgerData | null | undefined): Date | null => {
    if (!overrideData) return null;
    let dateStr = overrideData['Date']; // Prefer mm/dd/yyyy
    if (!dateStr) {
        dateStr = overrideData['date']; // Fallback to longer string
    }
    if (!dateStr) return null;

    try {
        const parts = dateStr.split('/');
        if (parts.length === 3) {
            const [month, day, year] = parts.map(Number);
            if (!isNaN(month) && !isNaN(day) && !isNaN(year)) {
                 const fullYear = year < 100 ? 2000 + year : year;
                 return new Date(fullYear, month - 1, day, 12, 0, 0); // Use noon
            }
        }
        const parsedDate = new Date(dateStr);
        return isNaN(parsedDate.getTime()) ? null : parsedDate;
    } catch (e) {
        console.error("Error parsing override date:", dateStr, e);
        return null;
    }
};


export const usePmt3FarmingBasicsData = () => {
  const { selectedCompany, selectedProduct } = usePMT3Selection();
  const [farmingBasicsData, setFarmingBasicsData] = useState<FarmingBasicsDisplayData | null>(null);
  const [groupAsins, setGroupAsins] = useState<GroupAsinInfo[]>([]);
  const [groupCount, setGroupCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      const currentAsin = selectedProduct?.id;
      if (!selectedCompany || !currentAsin) {
        setLoading(false);
        setFarmingBasicsData(null);
        setGroupAsins([]);
        setGroupCount(0);
        setError(null);
        return;
      }

      setLoading(true);
      setError(null);
      setFarmingBasicsData(null);
      setGroupAsins([]);
      setGroupCount(0);

      try {
        // --- Fetch primary data using service functions ---
        const results = await Promise.allSettled([
            fetchProductData(selectedCompany, currentAsin),           // 0: Product Data
            fetchFarmingHistoryData(selectedCompany, currentAsin),    // 1: Farming History
            fetchLatestOldValuesMap(selectedCompany, currentAsin),    // 2: Old Values Map
            fetchFarmingCostOverrideData(selectedCompany, currentAsin),// 3: Override Data
            fetchFarmingGroupData(selectedCompany, currentAsin)       // 4: Farming Group Data (for selected ASIN)
        ]);

        // Helper to get fulfilled value or null
        const getResult = <T>(index: number): T | null => {
            const result = results[index];
            if (result.status === 'fulfilled') {
                return result.value as T | null;
            } else {
                console.error(`Error fetching data at index ${index}:`, result.reason);
                return null;
            }
        };

        const productData = getResult<ProductData>(0);
        const farmingHistoryData = getResult<FarmingHistoryData>(1);
        const oldValues = getResult<Record<string, any>>(2) ?? {}; // Default to empty object
        const overrideData = getResult<OverrideLedgerData>(3);
        const farmingGroupData = getResult<FarmingGroupData>(4);

        // --- Process Override Data ---
        let overrideValue: number | string = '-';
        console.log('[FarmingBasics] Fetched oldValues:', oldValues); // DEBUG LOG
        console.log('[FarmingBasics] Fetched overrideData:', overrideData); // DEBUG LOG
        let overrideDaysAgo: number | string = '-';
        if (overrideData) {
            overrideValue = overrideData['New Value'] ?? '-';
            const overrideDate = parseOverrideDate(overrideData);
            if (overrideDate) {
                overrideDaysAgo = differenceInDays(new Date(), overrideDate);
            }
        }

        // --- Process Group Data (Fetch group ASINs based on initial group data) ---
        let currentFarmingGroup: number | null = null;
        let fetchedGroupAsins: GroupAsinInfo[] = [];
        console.log(`[FarmingBasics] Processed Override - Value: ${overrideValue}, Days Ago: ${overrideDaysAgo}`); // DEBUG LOG
        let fetchedGroupCount: number = 0;

        if (farmingGroupData && farmingGroupData['Farming Group'] !== undefined && farmingGroupData['Farming Group'] !== null) {
            currentFarmingGroup = Number(farmingGroupData['Farming Group']);
            if (!isNaN(currentFarmingGroup)) {
                // Fetch ASINs in the same group *after* getting the group number
                fetchedGroupAsins = await fetchGroupAsins(selectedCompany, currentFarmingGroup);
                fetchedGroupCount = fetchedGroupAsins.length;
            } else {
                 console.warn(`Farming Group value is not a valid number for ${selectedCompany}/${currentAsin} in pmt2_farming_groups`);
                 currentFarmingGroup = null; // Reset if not a number
            }
        } else {
            console.warn(`Farming Group data not found or missing 'Farming Group' field for ${selectedCompany}/${currentAsin} in pmt2_farming_groups`);
        }

        // --- Structure Display Data ---
        const displayData: FarmingBasicsDisplayData = {
          daysSinceFarming: productData?.['Days Since Farming'] ?? '-',
          timesFarmed: productData?.['Times Farmed'] ?? '-',
          timesFarmedOld: getNestedValue(oldValues, 'Times Farmed', '-'),
          timeFarmingActive: productData?.['Time Farming, if Actively Farming'] ?? '-',
          lifetimeDaysFarming: farmingHistoryData?.['Lifetime Days Farming'] ?? '-',
          lifetimeDaysFarmingOld: getNestedValue(oldValues, 'Lifetime Days Farming', '-'),
          lifetimeTermsFarming: 'NA',
          lifetimeTermsFarmingOld: getNestedValue(oldValues, 'Lifetime Terms from Farming', '-'),
          finalCostPerCst: productData?.['Final Farming Cost Per New CST'] ?? '-',
          finalCostPerCstOld: getNestedValue(oldValues, 'Final Farming Cost Per New CST', '-'),
          finalCostPerCstOverride: overrideValue,
          finalCostPerCstOverrideDaysAgo: overrideDaysAgo,
          farmingGroup: currentFarmingGroup !== null ? currentFarmingGroup : (productData?.['Farming Group'] ?? '-'),
          farmingGroupOld: getNestedValue(oldValues, 'Farming Group', '-'),
          otherAsinsInGroup: fetchedGroupCount > 0 ? fetchedGroupCount : '-',
          shareTermsInGroup: productData?.['Share Terms in Farming Group'] ?? '-',
          shareTermsInGroupOld: getNestedValue(oldValues, 'Share Terms in Farming Group', '-'),
          broadSeedsUrl: productData?.['Broad Seeds URL'] ?? null,
          broadSeedsType: productData?.['Broad Seeds Type'] ?? '-',
          broadSeedsTypeOld: getNestedValue(oldValues, 'Broad Seeds Type', '-'),
        };

        setFarmingBasicsData(displayData);
        setGroupAsins(fetchedGroupAsins); // Set the fetched group ASINs
        setGroupCount(fetchedGroupCount); // Set the fetched group count

      } catch (err) {
        console.error("Error fetching Farming Basics data:", err);
        setError(err instanceof Error ? err : new Error('An unknown error occurred'));
        setFarmingBasicsData(null);
        setGroupAsins([]);
        setGroupCount(0);
      } finally {
        setLoading(false);
      }
    };




    fetchData();
  }, [selectedCompany, selectedProduct]);

  return { farmingBasicsData, groupAsins, groupCount, loading, error };
};